import cx from 'classnames';
import {FC, MouseEventHandler, ReactNode, useCallback, useMemo} from 'react';
import {shallowEqual} from 'react-redux';
import {useIsMediumAndUp} from '~/common/hooks/useMatchQuery';
import {useCurrentRoute} from '~/QuickEcards/hooks/useCurrentRoute';
import {useNavigateToRoute} from '~/QuickEcards/hooks/useNavigateToRoute';
import {useAppSelector, useAppStore} from '~/QuickEcards/store';
import {selectGiftCardProductConfigId} from '~/QuickEcards/store/reducers/ecard';
import {
  selectGlobalMessage,
  selectHasCustomMessages,
} from '~/QuickEcards/store/reducers/recipientMessages';
import {Button} from '~library/atoms/Button';
import {ArrowIcon} from '~library/svg/ArrowIcon';

import './NavBottom.sass';

interface NavBottomProps {
  // biome-ignore lint/style/useNamingConvention: existing error
  InjectedComponent?: ReactNode;
  onNext?(): Promise<void>;
  onBack?(): Promise<void>;
}

export const NavBottom: FC<NavBottomProps> = ({InjectedComponent, onNext, onBack}) => {
  const store = useAppStore();
  const {currentRoute} = useCurrentRoute();
  const navigateToRoute = useNavigateToRoute();
  const mdScreens = useIsMediumAndUp();

  // Values that can affect the Next button text and variant;
  // include these in the dependency list for nextButtonText and nextButtonVariant
  const productConfigId = useAppSelector(selectGiftCardProductConfigId);
  const globalMessage = useAppSelector(selectGlobalMessage);
  const hasCustomMessages = useAppSelector(selectHasCustomMessages, shallowEqual);

  const handleBack: MouseEventHandler = useCallback(async () => {
    await onBack?.();
    await navigateToRoute('previous', store);
  }, [onBack, navigateToRoute, store]);

  const handleNext: MouseEventHandler = useCallback(async () => {
    await onNext?.();
    await navigateToRoute('next', store);
  }, [onNext, navigateToRoute, store]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const nextButtonText = useMemo(() => {
    if (!currentRoute?.nextButtonText) return 'Next';
    if (typeof currentRoute.nextButtonText === 'string') return currentRoute.nextButtonText;
    return currentRoute.nextButtonText(store);
  }, [currentRoute, store, globalMessage, hasCustomMessages, productConfigId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const nextButtonVariant = useMemo(() => {
    if (!currentRoute?.nextButtonVariant) return 'primary';
    if (typeof currentRoute.nextButtonVariant === 'string') return currentRoute.nextButtonVariant;
    return currentRoute.nextButtonVariant(store);
  }, [currentRoute, store, globalMessage, hasCustomMessages, productConfigId]);

  return (
    <div className="quick-ecards__nav-bottom">
      {InjectedComponent}
      {currentRoute?.previousRoute && (
        <Button
          variant="secondary"
          data-qa-id="nav-bottom-previous"
          size="large"
          onClick={handleBack}
          ariaLabel="Back"
        >
          <ArrowIcon direction="left" ratio={0.75} />
        </Button>
      )}
      <Button
        className={cx({'expand-next-button': mdScreens})}
        fullWidth
        onClick={handleNext}
        data-qa-id="nav-bottom-next"
        size="large"
        variant={nextButtonVariant}
      >
        {nextButtonText}
      </Button>
    </div>
  );
};
